import { useEffect, useState } from "react";

const CONNECT_EVENT = "connect";
const DISCONNECT_EVENT = "disconnect";

// Registers the socket for a feature's assistant room and reports whether the
// registration is live. Registering is idempotent on the server (a repeat register just
// acks again), so it is emitted on every connect with no client-side dedupe — a
// reconnect may land on a server instance holding no room state and always needs a
// fresh register. `isRegistered` drops on disconnect and flips back on the next ack,
// so consumers can resync whenever it turns true.
const useSocketRegistration = (socket, registerEvent, assistantId, enabled = true) => {
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    if (!socket || !assistantId || !enabled) {
      setIsRegistered(false);
      return undefined;
    }

    const register = () => socket.emit(registerEvent, { assistantId });
    const handleAck = (ack) => {
      if (ack?.assistantId === assistantId) setIsRegistered(true);
    };
    const handleDisconnect = () => setIsRegistered(false);

    socket.on(CONNECT_EVENT, register);
    socket.on(registerEvent, handleAck);
    socket.on(DISCONNECT_EVENT, handleDisconnect);
    if (socket.connected) register();

    return () => {
      socket.off(CONNECT_EVENT, register);
      socket.off(registerEvent, handleAck);
      socket.off(DISCONNECT_EVENT, handleDisconnect);
      setIsRegistered(false);
    };
  }, [socket, registerEvent, assistantId, enabled]);

  return isRegistered;
};

export default useSocketRegistration;
